import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { subscribeSharedFirestore } from './sharedFirestoreSubscription';

const quickWorksCollection = collection(firestore, 'quickWorks');

export const QUICK_WORK_CATEGORIES = [
  'GST Notice',
  'Income Tax Notice',
  'Letter',
  'CMA',
  'Appeal',
  'Projected Balance Sheet',
  'Certificate',
  'TDS Return Correction',
  'GST Refund',
  'GST Registration',
  'PAN/TAN Apply',
  'Misc. Registration',
  'Misc. Work',
];

export function subscribeQuickWorks({ role, userId, onData, onError }) {
  const normalizedRole = String(role || '').toLowerCase();
  const staffOnly = normalizedRole === 'staff';
  const quickWorkQuery = staffOnly
    ? query(quickWorksCollection, where('assignedToId', '==', userId))
    : query(quickWorksCollection);
  const key = staffOnly ? `quickWorks:staff:${userId}` : 'quickWorks:all';

  return subscribeSharedFirestore({
    key,
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(
      quickWorkQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        emit(rows);
      },
      emitError
    ),
  });
}

function dateOrNull(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function payload(values, clients, users) {
  const client = clients.find((item) => item.id === values.clientId);
  const assignee = users.find((item) => item.id === values.assignedToId);
  return {
    clientId: values.clientId,
    clientName: client?.name || client?.clientName || '',
    nature: values.nature,
    remarks: values.remarks?.trim() || '',
    assignedToId: values.assignedToId || '',
    assignedToName: assignee?.name || assignee?.username || assignee?.email || '',
    dueDate: dateOrNull(values.dueDate),
    status: values.status || 'Pending',
  };
}

export async function createQuickWork(values, clients, users, creator) {
  const record = payload(values, clients, users);
  if (!record.assignedToName && record.assignedToId && record.assignedToId === (creator?.id || '')) {
    record.assignedToName = creator?.name || creator?.username || creator?.email || '';
  }
  return addDoc(quickWorksCollection, {
    ...record,
    billingStatus: 'Not Billed',
    billingCompleted: false,
    proformaId: '',
    proformaNumber: '',
    completedAt: values.status === 'Completed' ? serverTimestamp() : null,
    createdById: creator?.id || '',
    createdByName: creator?.name || creator?.username || creator?.email || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateQuickWork(id, values, clients, users, existing = {}) {
  const completedNow = values.status === 'Completed' && existing.status !== 'Completed';
  return updateDoc(doc(firestore, 'quickWorks', id), {
    ...payload(values, clients, users),
    completedAt: completedNow ? serverTimestamp() : existing.completedAt || null,
    updatedAt: serverTimestamp(),
  });
}

export async function updateOwnQuickWork(id, values, existing = {}) {
  const completedNow = values.status === 'Completed' && existing.status !== 'Completed';
  return updateDoc(doc(firestore, 'quickWorks', id), {
    status: values.status,
    remarks: values.remarks?.trim() || '',
    completedAt: completedNow ? serverTimestamp() : existing.completedAt || null,
    updatedAt: serverTimestamp(),
  });
}
